import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { XCircle, Plus, ArrowsDownUp, ChartLineDown, ArrowCounterClockwise } from "@phosphor-icons/react";

const DEFAULTS = [
  "Price / Fees too high",
  "Chose another firm",
  "No response from client",
  "Out of scope / Cannot assist",
  "Duplicate enquiry",
  "Not a real lead / Spam",
];

export default function LostReasonsTab({ settings, onSaved }) {
  const [reasons, setReasons] = useState(
    Array.isArray(settings?.lost_reasons) && settings.lost_reasons.length > 0
      ? settings.lost_reasons
      : DEFAULTS,
  );
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const persist = async (list) => {
    setSaving(true);
    try {
      await api.patch("/settings", { lost_reasons: list });
      toast.success("Lost reasons saved");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const addReason = () => {
    const v = draft.trim();
    if (!v) return;
    if (reasons.some((r) => r.toLowerCase() === v.toLowerCase())) {
      toast.error("Already in the list");
      return;
    }
    const next = [...reasons, v];
    setReasons(next);
    setDraft("");
    persist(next);
  };

  const removeReason = (idx) => {
    const next = reasons.filter((_, i) => i !== idx);
    setReasons(next);
    persist(next);
  };

  const moveReason = (from, to) => {
    if (to < 0 || to >= reasons.length) return;
    const next = [...reasons];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setReasons(next);
    persist(next);
  };

  const resetDefaults = async () => {
    if (!window.confirm("Reset lost reasons to the default list?")) return;
    setReasons(DEFAULTS);
    persist(DEFAULTS);
  };

  return (
    <div data-testid="lost-reasons-tab" className="space-y-8 max-w-3xl">
      <section className="bg-white border border-slate-200 rounded-sm p-6">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="h-12 w-12 rounded-sm bg-rose-50 flex items-center justify-center shrink-0">
            <ChartLineDown size={24} className="text-rose-600" weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-2xl text-slate-900">Lost Reasons</h2>
            <p className="text-sm text-slate-500 mt-1">
              These reasons appear in a dropdown when an attorney marks a lead as <span className="font-medium text-slate-700">Lost</span>.
              Use them in Analytics to track <em>why</em> leads slip away.
            </p>
          </div>
          <Button
            data-testid="reset-lost-reasons-btn"
            variant="outline"
            size="sm"
            className="rounded-sm gap-2"
            onClick={resetDefaults}
            disabled={saving}
          >
            <ArrowCounterClockwise size={12} /> Reset
          </Button>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-sm">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
          <div className="label-tracking">Active reasons ({reasons.length})</div>
        </div>
        {reasons.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No lost reasons configured.</div>
        ) : (
          <ul>
            {reasons.map((r, idx) => (
              <li
                key={`${r}-${idx}`}
                data-testid={`lost-reason-row-${idx}`}
                className="px-6 py-3 flex items-center gap-3 border-b border-slate-100 last:border-b-0 group"
              >
                <span className="text-slate-300 font-mono text-xs w-6 text-right">{idx + 1}.</span>
                <span className="flex-1 text-sm text-slate-800">{r}</span>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    title="Move up"
                    onClick={() => moveReason(idx, idx - 1)}
                    disabled={idx === 0}
                    className="text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed p-1"
                  >
                    <ArrowsDownUp size={14} />
                  </button>
                  <button
                    type="button"
                    data-testid={`remove-lost-reason-${idx}`}
                    onClick={() => removeReason(idx)}
                    className="text-slate-400 hover:text-rose-600 p-1"
                    title="Remove"
                  >
                    <XCircle size={16} weight="fill" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <div className="p-4 border-t border-slate-100 flex gap-2 bg-slate-50/40">
          <Input
            data-testid="new-lost-reason-input"
            placeholder="Add a new reason… e.g. Timing not right"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addReason(); }}
            className="h-10 rounded-sm border-slate-300 text-sm focus-visible:ring-rose-500 focus-visible:ring-2"
          />
          <Button
            data-testid="add-lost-reason-btn"
            onClick={addReason}
            disabled={saving || !draft.trim()}
            className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-1.5 h-10"
          >
            <Plus size={14} /> Add
          </Button>
        </div>
      </section>
    </div>
  );
}
